import { useEffect, useRef, useState } from "react";

import { READ_DEBOUNCE_MS } from "../use-effective";
import { searchWorkspaces, type WorkspaceMatch } from "./workspace-search-api";

/**
 * The `MIN_TERM_LENGTH` the lookup route enforces
 * (`services/identity/src/routes/admin-workspaces.ts`).
 *
 * Held here as well rather than only there, because a shorter term is a
 * request the console already knows will be refused: sending it costs a round
 * trip, an audited disclosure of nothing, and a validation error where the
 * operator wanted a hint. Three is not arbitrary, it is a trigram, which is
 * what migration 061's GIN index can look up.
 */
export const MIN_SEARCH_LENGTH = 3;

export interface UseWorkspaceSearchReturn {
  /** Null until a search has answered; an empty array is a real "no matches". */
  readonly matches: readonly WorkspaceMatch[] | null;
  readonly isSearching: boolean;
  readonly error: string | null;
  /** The operator has typed something, but not yet enough to search on. */
  readonly tooShort: boolean;
}

interface SearchState {
  /** The term this state answers for. State for any other term is not an answer. */
  readonly term: string;
  readonly matches: readonly WorkspaceMatch[] | null;
  readonly error: string | null;
  readonly isSearching: boolean;
}

/**
 * Find a workspace by name, debounced, with the route's minimum enforced here.
 *
 * Every result is stored WITH the term it answers and dropped on arrival if the
 * operator has typed on, which is what keeps a slow "iron" answer from
 * appearing under "oakwood" and being clicked. A request-id counter cannot
 * close that window: it would be invalidated in an effect, effects run after
 * paint, and an answer landing in that commit is accepted against the previous
 * term. Picking the wrong row here selects the wrong customer's scope.
 */
export function useWorkspaceSearch(term: string): UseWorkspaceSearchReturn {
  const trimmed = term.trim();
  const searchable = trimmed.length >= MIN_SEARCH_LENGTH;

  /* Assigned during RENDER, not in an effect, for the reason in the docblock
     above. The assignment is idempotent and touches nothing outside this
     component. */
  const currentTerm = useRef(trimmed);
  currentTerm.current = trimmed;

  const [state, setState] = useState<SearchState>({
    term: trimmed,
    matches: null,
    error: null,
    isSearching: false,
  });

  useEffect(() => {
    if (!searchable) {
      setState({ term: trimmed, matches: null, error: null, isSearching: false });
      return;
    }

    setState({ term: trimmed, matches: null, error: null, isSearching: true });
    const timer = setTimeout(() => {
      searchWorkspaces(trimmed)
        .then((matches) => {
          if (currentTerm.current !== trimmed) return;
          setState({ term: trimmed, matches, error: null, isSearching: false });
        })
        .catch((cause: unknown) => {
          if (currentTerm.current !== trimmed) return;
          setState({
            term: trimmed,
            matches: null,
            error: cause instanceof Error ? cause.message : "Workspace search failed",
            isSearching: false,
          });
        });
    }, READ_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [trimmed, searchable]);

  const tooShort = trimmed.length > 0 && !searchable;

  if (state.term === trimmed) {
    return {
      matches: state.matches,
      isSearching: state.isSearching,
      error: state.error,
      tooShort,
    };
  }
  /* The term changed and the effect has not run yet: report the search as
     pending rather than handing back the previous term's matches. */
  return { matches: null, isSearching: searchable, error: null, tooShort };
}
